/* global window, document, AbortController, URLSearchParams */
import { Controller } from '@hotwired/stimulus'
import mapboxgl from 'mapbox-gl'

const HUB_COLOR = '#24A718'
const HUB_SELECTED_COLOR = '#1F2937'
const POINTER_IMAGE = 'transit-pointer'
const BADGE_RADIUS = 11
const SDF_RADIUS = 8
const SDF_CUTOFF = 0.25
const MAX_NUMBER_LENGTH = 3
// Vehicles are small dots when zoomed out and full badges, with their line
// number, from this zoom up.
const NUMBER_MIN_ZOOM = 14
const REFRESH_MS = 30000

export default class extends Controller {
  static targets = [
    'map', 'kpiFeeds', 'kpiVehicles', 'kpiVehiclesOnRoutes', 'kpiStops', 'kpiAge',
    'listView', 'detailView', 'search', 'hubList', 'hubTitle',
    'message', 'arrivalsTable', 'arrivalsBody', 'hubOnly'
  ]

  static values = { dataUrl: String, hubUrl: String, pageUrl: String, initialHub: String }

  connect () {
    this.hubSlug = this.initialHubValue || null
    this.payload = null
    this.detail = null
    this.mapReady = false
    this.requestId = 0
    this.detailRequestId = 0
    this.aborter = null
    this.initMap()
    this.onPopState = () => {
      const slug = new URLSearchParams(window.location.search).get('hub')
      this.applySelection(slug || null)
    }
    window.addEventListener('popstate', this.onPopState)
    this.timer = window.setInterval(() => {
      if (!document.hidden) this.refresh()
    }, REFRESH_MS)
    this.applySelection(this.hubSlug)
  }

  disconnect () {
    window.clearInterval(this.timer)
    window.removeEventListener('popstate', this.onPopState)
    if (this.aborter) this.aborter.abort()
    if (this.detailAborter) this.detailAborter.abort()
    if (this.map) this.map.remove()
  }

  // ----- selection -----

  selectHub (slug) {
    if (slug === this.hubSlug) return
    window.history.pushState({}, '', `${this.pageUrlValue}?hub=${encodeURIComponent(slug)}`)
    this.applySelection(slug)
  }

  clearHub () {
    window.history.pushState({}, '', this.pageUrlValue)
    this.applySelection(null)
  }

  applySelection (slug) {
    this.hubSlug = slug
    this.detail = null
    this.listViewTarget.hidden = slug !== null
    this.detailViewTarget.hidden = slug === null
    this.hubOnlyTargets.forEach((node) => { node.hidden = slug === null })
    this.clearDetailLayers()
    if (slug) {
      this.hubTitleTarget.textContent = this.hubName(slug) || slug
      this.showMessage('Loading arrivals…')
      this.loadDetail(slug)
    }
    this.renderMap()
    this.refresh()
  }

  // ----- data -----

  async refresh () {
    if (this.aborter) this.aborter.abort()
    this.aborter = new AbortController()
    const requestId = ++this.requestId
    const url = this.hubSlug ? `${this.dataUrlValue}?hub=${encodeURIComponent(this.hubSlug)}` : this.dataUrlValue
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: this.aborter.signal })
      if (!response.ok) throw new Error(`Transit request failed (${response.status})`)
      const payload = await response.json()
      if (requestId !== this.requestId) return
      this.payload = payload
      this.render()
    } catch (error) {
      if (error.name === 'AbortError') return
      if (this.hubSlug) this.showMessage('Could not load arrivals. Retrying…')
    }
  }

  async loadDetail (slug) {
    if (this.detailAborter) this.detailAborter.abort()
    this.detailAborter = new AbortController()
    const requestId = ++this.detailRequestId
    try {
      const url = this.hubUrlValue.replace('__SLUG__', encodeURIComponent(slug))
      const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: this.detailAborter.signal })
      if (!response.ok) throw new Error(`Hub request failed (${response.status})`)
      const detail = await response.json()
      if (requestId !== this.detailRequestId || slug !== this.hubSlug) return
      this.detail = detail
      this.renderDetail()
    } catch (error) {
      if (error.name === 'AbortError') return
      this.showMessage('Could not load this hub.')
    }
  }

  // ----- rendering -----

  render () {
    if (!this.payload) return
    this.renderKpis()
    this.renderHubList()
    this.renderArrivals()
    this.renderMap()
  }

  renderKpis () {
    const { kpis } = this.payload
    this.kpiFeedsTarget.textContent = `${kpis.feeds_up} / ${kpis.feeds_total}`
    this.kpiVehiclesTarget.textContent = kpis.vehicles_tracked.toLocaleString()
    this.kpiVehiclesOnRoutesTarget.textContent = kpis.vehicles_on_hub_routes.toLocaleString()
    this.kpiStopsTarget.textContent = kpis.hub_stops.toLocaleString()
    this.kpiAgeTarget.textContent = formatAge(kpis.median_feed_age_seconds)
  }

  renderHubList () {
    const term = this.searchTarget.value.trim().toLowerCase()
    const hubs = this.payload.hubs.filter((hub) => hub.name.toLowerCase().includes(term))
    const rows = hubs.map((hub) => {
      const button = el('button', 'transit-hub-row')
      button.type = 'button'
      button.dataset.slug = hub.slug
      button.append(el('span', null, hub.name))
      button.append(el('span', 'transit-hub-meta', hub.stops_mapped === 0 ? 'no stops mapped' : `${hub.stops_mapped} stop${hub.stops_mapped === 1 ? '' : 's'}`))
      button.addEventListener('click', () => this.selectHub(hub.slug))
      return button
    })
    this.hubListTarget.replaceChildren(...rows)
  }

  filterHubs () {
    if (this.payload) this.renderHubList()
  }

  renderArrivals () {
    if (!this.hubSlug || !this.payload.arrivals) return
    const selected = this.payload.selected_hub
    if (selected) this.hubTitleTarget.textContent = selected.name

    if (selected && selected.stops_mapped === 0) {
      this.showMessage('No transit stops mapped for this hub.')
      return
    }

    const { rows } = this.payload.arrivals
    if (rows.length === 0) {
      this.showMessage('No upcoming arrivals in the next 60 minutes.')
      return
    }

    this.messageTarget.hidden = true
    this.arrivalsBodyTarget.replaceChildren(...rows.map((row) => arrivalRow(row)))
    this.arrivalsTableTarget.hidden = false
  }

  showMessage (text) {
    this.arrivalsTableTarget.hidden = true
    this.messageTarget.textContent = text
    this.messageTarget.hidden = false
  }

  hubName (slug) {
    const hub = this.payload && this.payload.hubs.find((candidate) => candidate.slug === slug)
    return hub && hub.name
  }

  // ----- map -----

  initMap () {
    mapboxgl.accessToken = window.mapbox_data.access_token
    const center = window.mapbox_data.coordinates || [-118.2437, 34.0522]
    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: 'mapbox://styles/mapbox/streets-v12',
      center,
      zoom: 11
    })
    this.map.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
    this.map.on('load', () => {
      this.addPointerImage()
      this.addLayers()
      this.mapReady = true
      this.renderMap()
      this.renderDetail()
    })
  }

  // The heading pointer: a triangle above the badge circle. The badge itself is
  // a circle layer (GPU-antialiased); this icon rotates around its centre, which
  // is the badge centre. It is a real signed distance field (not a drawn
  // bitmap), which is what Mapbox expects for sdf icons and why it stays smooth
  // when scaled and rotated.
  addPointerImage () {
    const size = 64
    const centre = size / 2
    const tip = [centre, centre - BADGE_RADIUS - 6]
    const left = [centre - 8, centre - BADGE_RADIUS + 2]
    const right = [centre + 8, centre - BADGE_RADIUS + 2]
    const data = new Uint8Array(size * size * 4)

    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const distance = triangleDistance([x + 0.5, y + 0.5], tip, right, left)
        const alpha = 255 - 255 * (distance / SDF_RADIUS + SDF_CUTOFF)
        data[(y * size + x) * 4 + 3] = Math.max(0, Math.min(255, Math.round(alpha)))
      }
    }

    this.map.addImage(POINTER_IMAGE, { width: size, height: size, data }, { sdf: true })
  }

  addLayers () {
    const empty = { type: 'FeatureCollection', features: [] }
    ;['hubs', 'routes', 'stops', 'vehicles'].forEach((id) => this.map.addSource(id, { type: 'geojson', data: empty }))

    this.map.addLayer({
      id: 'routes',
      type: 'line',
      source: 'routes',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': ['get', 'color'], 'line-width': 4, 'line-opacity': 0.6 }
    })
    this.map.addLayer({
      id: 'stops',
      type: 'circle',
      source: 'stops',
      paint: {
        'circle-radius': 7,
        'circle-color': '#1F2937',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      }
    })
    this.map.addLayer({
      id: 'stop-numbers',
      type: 'symbol',
      source: 'stops',
      layout: {
        'text-field': ['to-string', ['get', 'number']],
        'text-size': 10,
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-allow-overlap': true
      },
      paint: { 'text-color': '#ffffff' }
    })
    this.map.addLayer({
      id: 'hubs',
      type: 'circle',
      source: 'hubs',
      paint: {
        'circle-radius': ['case', ['get', 'selected'], 16, 12],
        'circle-color': ['case', ['get', 'selected'], HUB_SELECTED_COLOR, HUB_COLOR],
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff'
      }
    })
    this.map.addLayer({
      id: 'hub-labels',
      type: 'symbol',
      source: 'hubs',
      minzoom: 12,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 13,
        'text-offset': [0, 1.8],
        'text-anchor': 'top',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular']
      },
      paint: { 'text-color': '#1F2937', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 }
    })
    this.map.addLayer({
      id: 'vehicle-pointers',
      type: 'symbol',
      source: 'vehicles',
      filter: ['get', 'has_bearing'],
      layout: {
        'icon-image': POINTER_IMAGE,
        'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.3, 12, 0.5, NUMBER_MIN_ZOOM, 1],
        'icon-rotate': ['get', 'bearing'],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true
      },
      paint: { 'icon-color': ['get', 'color'], 'icon-halo-color': '#ffffff', 'icon-halo-width': 1.5 }
    })
    this.map.addLayer({
      id: 'vehicles',
      type: 'circle',
      source: 'vehicles',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, BADGE_RADIUS * 0.3, 12, BADGE_RADIUS * 0.5, NUMBER_MIN_ZOOM, BADGE_RADIUS],
        'circle-color': ['get', 'color'],
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 10, 1, NUMBER_MIN_ZOOM, 2],
        'circle-stroke-color': '#ffffff'
      }
    })
    this.map.addLayer({
      id: 'vehicle-numbers',
      type: 'symbol',
      source: 'vehicles',
      minzoom: NUMBER_MIN_ZOOM,
      filter: ['<=', ['length', ['get', 'line']], MAX_NUMBER_LENGTH],
      layout: {
        'text-field': ['get', 'line'],
        'text-size': ['step', ['length', ['get', 'line']], 12, 3, 10],
        'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
        // Badges that sit on top of each other can't both show a number: the
        // most recently reported vehicle keeps its number.
        'symbol-sort-key': ['get', 'age_seconds'],
        'text-padding': 1
      },
      paint: { 'text-color': ['get', 'text_color'] }
    })
    this.map.addLayer({
      id: 'vehicle-labels',
      type: 'symbol',
      source: 'vehicles',
      minzoom: NUMBER_MIN_ZOOM,
      filter: ['>', ['length', ['get', 'line']], MAX_NUMBER_LENGTH],
      layout: {
        'text-field': ['get', 'line'],
        'text-size': 12,
        // text-offset is in ems: badge radius (11 px) plus a small gap, over the 12 px text size
        'text-offset': [0, (BADGE_RADIUS + 3) / 12],
        'text-anchor': 'top',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular']
      },
      paint: { 'text-color': '#1F2937', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 }
    })

    this.map.on('click', 'hubs', (event) => {
      const slug = event.features[0].properties.slug
      if (slug) this.selectHub(slug)
    })
    ;['hubs'].forEach((layer) => {
      this.map.on('mouseenter', layer, () => { this.map.getCanvas().style.cursor = 'pointer' })
      this.map.on('mouseleave', layer, () => { this.map.getCanvas().style.cursor = '' })
    })
    ;['vehicles', 'vehicle-numbers'].forEach((layer) => {
      this.map.on('click', layer, (event) => this.showVehiclePopup(event))
    })
  }

  showVehiclePopup (event) {
    const properties = event.features[0].properties
    const node = el('div', 'transit-popup-body')
    const title = el('div', 'transit-popup-title')
    title.append(chip(properties.line, properties.color))
    node.append(title)
    node.append(el('div', 'transit-popup-agency', properties.agency))
    if (properties.headsign) node.append(el('div', 'transit-popup-line', `→ ${properties.headsign}`))
    node.append(el('div', 'transit-popup-meta', `reported ${formatAge(Number(properties.age_seconds))} ago`))
    new mapboxgl.Popup({ offset: 14, className: 'transit-popup', maxWidth: '260px' })
      .setLngLat(event.features[0].geometry.coordinates)
      .setDOMContent(node)
      .addTo(this.map)
  }

  renderMap () {
    if (!this.mapReady || !this.payload) return
    // With a hub selected, only that hub stays on the map.
    const visibleHubs = this.payload.hubs.filter((hub) => !this.hubSlug || hub.slug === this.hubSlug)
    this.map.getSource('hubs').setData({
      type: 'FeatureCollection',
      features: visibleHubs.map((hub) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [hub.longitude, hub.latitude] },
        properties: { slug: hub.slug, name: hub.name, selected: hub.slug === this.hubSlug }
      }))
    })
    this.map.getSource('vehicles').setData({
      type: 'FeatureCollection',
      features: this.payload.vehicles.map((vehicle) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [vehicle.longitude, vehicle.latitude] },
        properties: {
          line: vehicle.line,
          agency: vehicle.agency,
          headsign: vehicle.headsign,
          color: vehicle.color,
          text_color: vehicle.text_color,
          bearing: vehicle.bearing || 0,
          has_bearing: vehicle.bearing !== null && vehicle.bearing !== undefined,
          age_seconds: vehicle.age_seconds
        }
      }))
    })

    if (!this.fittedAll && !this.hubSlug && this.payload.hubs.length > 0) {
      this.fittedAll = true
      this.fit(this.payload.hubs.map((hub) => [hub.longitude, hub.latitude]))
    }
  }

  renderDetail () {
    if (!this.mapReady || !this.detail) return
    this.map.getSource('stops').setData({
      type: 'FeatureCollection',
      features: this.detail.stops.map((stop) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [stop.longitude, stop.latitude] },
        properties: { number: stop.number, name: stop.name }
      }))
    })
    this.map.getSource('routes').setData(this.detail.routes)

    const points = this.detail.stops.map((stop) => [stop.longitude, stop.latitude])
    if (points.length === 0) points.push([this.detail.hub.longitude, this.detail.hub.latitude])
    this.fit(points)
  }

  clearDetailLayers () {
    if (!this.mapReady) return
    const empty = { type: 'FeatureCollection', features: [] }
    this.map.getSource('stops').setData(empty)
    this.map.getSource('routes').setData(empty)
    if (!this.hubSlug && this.payload) {
      this.fit(this.payload.hubs.map((hub) => [hub.longitude, hub.latitude]))
    }
  }

  fit (points) {
    if (points.length === 0) return
    const bounds = points.reduce((box, point) => box.extend(point), new mapboxgl.LngLatBounds(points[0], points[0]))
    this.map.fitBounds(bounds, { padding: 64, maxZoom: 16, duration: 600 })
  }
}

// ----- helpers -----

function el (tag, className, text) {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text !== undefined && text !== null) node.textContent = text
  return node
}

function formatAge (seconds) {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 120) return `${seconds} s`
  return `${Math.round(seconds / 60)} min`
}

function formatEta (minutes) {
  return minutes < 1 ? 'Now' : `${minutes} min`
}

function chip (line, color) {
  const node = el('span', 'transit-line-chip', line)
  if (color) node.style.backgroundColor = color
  return node
}

function arrivalRow (row) {
  const tr = el('tr')
  const lineCell = el('td')
  lineCell.append(chip(row.line, row.color))
  const infoCell = el('td')
  infoCell.append(el('div', null, row.agency))
  const detail = [row.headsign && `→ ${row.headsign}`, row.stop_name].filter(Boolean).join(' · ')
  infoCell.append(el('div', 'transit-arrival-sub', detail))
  const etaCell = el('td', `transit-eta${row.mode === 'rail' ? ' transit-eta--rail' : ''}`, row.minutes.map(formatEta).join(' · '))
  tr.append(lineCell, infoCell, etaCell)
  return tr
}

// Signed distance from `point` to the triangle a-b-c (negative inside).
function triangleDistance (point, a, b, c) {
  const edges = [[a, b], [b, c], [c, a]]
  const distance = Math.min(...edges.map(([from, to]) => segmentDistance(point, from, to)))
  const signs = edges.map(([from, to]) => (to[0] - from[0]) * (point[1] - from[1]) - (to[1] - from[1]) * (point[0] - from[0]))
  const inside = signs.every((sign) => sign >= 0) || signs.every((sign) => sign <= 0)
  return inside ? -distance : distance
}

function segmentDistance (point, from, to) {
  const dx = to[0] - from[0]
  const dy = to[1] - from[1]
  const t = Math.max(0, Math.min(1, ((point[0] - from[0]) * dx + (point[1] - from[1]) * dy) / (dx * dx + dy * dy)))
  return Math.hypot(point[0] - (from[0] + t * dx), point[1] - (from[1] + t * dy))
}
